import React from 'react';
import { Tooltip as AntdTooltip } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AccountReport } from '../function/type';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface PerformanceAnalysisProps {
  data: AccountReport | undefined | null;
}

// Component Overlay hiển thị thông báo "Không có dữ liệu"
const EmptyDataOverlay: React.FC<{ show: boolean }> = ({ show }) => {
  if (!show) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px] z-10 pointer-events-none">
      <span className="text-xs sm:text-sm font-medium text-slate-400 bg-slate-50/90 border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm">
        Không có dữ liệu
      </span>
    </div>
  );
};

const PerformanceAnalysis: React.FC<PerformanceAnalysisProps> = ({ data }) => {
  // Mock data for Volume & Winrate
  const timeInWeekData = data?.data?.time_analysis?.daily_analysis || [];
  const timeInDayData = data?.data?.time_analysis?.hourly_analysis || [];
  const symbolData = data?.data?.symbol_analysis || [];

  const winrateData = {
    winTrade: data?.data?.performance_summary?.profitable_trades || 0,
    loseTrade: data?.data?.performance_summary?.losing_trades || 0,
  };

  // Kiểm tra điều kiện rỗng cho từng biểu đồ
  const isWeekDataEmpty = timeInWeekData.length === 0;
  const isWinrateEmpty = winrateData.winTrade === 0 && winrateData.loseTrade === 0;
  const isDayDataEmpty = timeInDayData.length === 0;
  const isSymbolDataEmpty = symbolData.length === 0;

  const volWinrateData = {
    labels: timeInWeekData.map(item => item.day),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Lợi nhuận / Lỗ (Profit/Loss)',
        data: timeInWeekData.map(item => item.profit),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderRadius: 4,
      },
      {
        type: 'line' as const,
        label: 'Winrate (%)',
        data: timeInWeekData.map(item => item.win_rate),
        borderColor: '#14b8a6',
        borderWidth: 2,
        pointBackgroundColor: '#14b8a6',
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: { boxWidth: 12, font: { size: 10, weight: 'bold' as any } },
      },
      tooltip: {
        padding: 10,
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 10 } } },
    },
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Khối lượng & Winrate */}
      <div className="bg-white rounded-2xl shadow-premium border border-slate-100 p-6 sm:p-8">
        <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
          <h2 className="!text-base font-bold !text-slate-900 !mb-0">Khối lượng giao dịch & Hiệu suất</h2>
          <AntdTooltip title="Biểu đồ kết hợp: cột thể hiện Lợi nhuận/Lỗ (Profit/Loss) và đường thể hiện Tỷ lệ thắng (Winrate %) theo từng ngày trong tuần, giúp bạn so sánh nhanh hiệu suất giữa các ngày giao dịch.">
            <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </div>
          </AntdTooltip>
        </div>
        <div className="relative h-[250px] w-full">
          <Bar
            data={volWinrateData as any}
            options={{
              ...commonOptions,
              scales: {
                ...commonOptions.scales,
                y1: {
                  type: 'linear' as const,
                  display: true,
                  position: 'right' as const,
                  grid: { drawOnChartArea: false },
                  min: 0,
                  max: 100,
                  ticks: { callback: (value) => `${value}%` }
                }
              }
            }}
          />
          <EmptyDataOverlay show={isWeekDataEmpty} />
        </div>
      </div>

      {/* Lãi Lỗ TB & Khung giờ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-premium border border-slate-100 p-6 sm:p-8">
          <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
            <h2 className="!text-base font-bold !text-slate-900 !mb-0">Lãi / Lỗ trung bình</h2>
            <AntdTooltip title="So sánh số lượng lệnh thắng và số lượng lệnh thua trong khoảng thời gian đang xem, giúp bạn nhìn nhanh sự chênh lệch giữa hai nhóm lệnh">
              <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </div>
            </AntdTooltip>
          </div>
          <div className="relative h-[200px] w-full">
            <Bar
              data={{
                labels: ['Số Lệnh Thắng', 'Số Lệnh Thua'],
                datasets: [{
                  data: [winrateData.winTrade, winrateData.loseTrade],
                  backgroundColor: ['#14b8a6', '#f43f5e'],
                  borderRadius: 6,
                }]
              }}
              options={{
                ...commonOptions,
                indexAxis: 'y' as const,
                plugins: { ...commonOptions.plugins, legend: { display: false } }
              }}
            />
            <EmptyDataOverlay show={isWinrateEmpty} />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-premium border border-slate-100 p-6 sm:p-8">
          <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
            <h2 className="!text-base font-bold !text-slate-900 !mb-0">Tỷ lệ Thắng theo Khung giờ</h2>
            <AntdTooltip title="Tỷ lệ thắng trung bình tính theo từng khung giờ trong ngày, giúp bạn xác định những khung giờ giao dịch hiệu quả nhất.">
              <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </div>
            </AntdTooltip>
          </div>
          <div className="relative h-[200px] w-full">
            <Bar
              data={{
                labels: timeInDayData.map(item => item.hour),
                datasets: [{
                  data: timeInDayData.map(item => item.win_rate),
                  backgroundColor: '#6366f1',
                  borderRadius: 6,
                }]
              }}
              options={{
                ...commonOptions,
                plugins: { ...commonOptions.plugins, legend: { display: false } },
                scales: { ...commonOptions.scales, y: { ...commonOptions.scales.y, max: 100 } }
              }}
            />
            <EmptyDataOverlay show={isDayDataEmpty} />
          </div>
        </div>
      </div>

      {/* Sản phẩm */}
      <div className="bg-white rounded-2xl shadow-premium border border-slate-100 p-6 sm:p-8">
        <div className="flex justify-start gap-2 items-center mb-2 z-10 relative">
          <h2 className="!text-base font-bold !text-slate-900 !mb-0">Hiệu suất theo Sản phẩm (WinRate)</h2>
          <AntdTooltip title="Tỷ lệ thắng trung bình theo từng sản phẩm/cặp giao dịch, giúp bạn so sánh hiệu suất giữa các sản phẩm khác nhau để tối ưu lựa chọn.">
            <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </div>
          </AntdTooltip>
        </div>
        <div className="relative h-[200px] w-full">
          <Bar
            data={{
              labels: symbolData.map(item => item.symbol),
              datasets: [{
                data: symbolData.map(item => item.win_rate),
                backgroundColor: '#3b82f6',
                borderRadius: 4,
              }]
            }}
            options={{
              ...commonOptions,
              plugins: { ...commonOptions.plugins, legend: { display: false } },
              scales: { ...commonOptions.scales, y: { ...commonOptions.scales.y, max: 100 } }
            }}
          />
          <EmptyDataOverlay show={isSymbolDataEmpty} />
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalysis; 